/**
 * Reveals elements carrying a `data-animate` attribute with a subtle fade/slide transition
 * as they scroll into view (elements already in the viewport on load reveal right away).
 *
 * Usage: `data-animate="fade-up | fade-in | fade-left | fade-right | scale-in"`,
 * optionally paired with `data-animate-delay="150"` (ms) to stagger a group of elements.
 * Respects `prefers-reduced-motion` via the CSS in base.css.
 */

const SELECTOR = '[data-animate]';
const REVEALED_CLASS = 'is-visible';

/** @type {IntersectionObserver | undefined} */
let observer;

function revealImmediately() {
  document.querySelectorAll(SELECTOR).forEach((element) => element.classList.add(REVEALED_CLASS));
}

function observe() {
  const elements = document.querySelectorAll(`${SELECTOR}:not(.${REVEALED_CLASS})`);
  if (!elements.length) return;

  if (!('IntersectionObserver' in window)) {
    revealImmediately();
    return;
  }

  observer ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        const target = entry.target;
        const delay = target.getAttribute('data-animate-delay');

        if (delay) target.style.setProperty('--reveal-delay', `${delay}ms`);

        target.classList.add(REVEALED_CLASS);
        observer?.unobserve(target);
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
  );

  elements.forEach((element) => observer.observe(element));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observe);
} else {
  observe();
}

// Re-scan when the theme editor adds, reorders, or re-renders a section.
document.addEventListener('shopify:section:load', observe);
